/* ============================================================================
   susanna-ajanvaraus.js — the sticky bar must not sell time it has not booked
   ----------------------------------------------------------------------------
   Written 2026-09-22 (docs/open-work.md §21).

   THE BUG THIS EXISTS FOR. Easy Appointment Booking replaces the product form's
   "Lisää ostoskoriin" with its own "Valitse aika", and its *Hide Add to cart
   button* setting hides the original. That setting reaches exactly one button:
   the one inside `shopify-product-form`. Horizon ships a SECOND one — the
   sticky bar that follows the customer down the page — and the app has never
   heard of it. Verified on the live store by doing it: one click on the sticky
   bar put `Yksittäinen tapaaminen 60 min`, 145 €, into the cart with
   `properties: {}` — no time, no booking, nothing to tell anyone it happened.
   That is the failure the plan calls the worst possible one on this site.

   WHAT THIS DOES. On a product that has a booking picker, the sticky bar stops
   adding to cart and opens the picker instead — the same action as the main
   button, which is what a customer reaching for a persistent CTA expects. The
   bar keeps doing its job on a long page; it just stops doing the wrong one.

   WHAT IT DELIBERATELY DOES NOT DO. Package products (`Selkeyden
   starttipaketti`, `Työkalut suunnanmuutokseen`) have NO picker, because an EAB
   package is prepaid — you buy credits and redeem them later. There, add to
   cart is the correct action and the sticky bar is left completely alone. The
   check is structural rather than a hardcoded list of handles: a page counts as
   bookable only if the app actually rendered a picker button on it, so a new
   bookable product is covered automatically and a new package is not touched.

   ⚠️ WHY THIS REPLACES THE BUTTON INSTEAD OF INTERCEPTING THE CLICK, which is
   the obvious approach and was the first attempt. Horizon binds the sticky
   button declaratively (`on:click="/handleAddToCartClick"` on the
   `<sticky-add-to-cart>` element) and handles it **at window capture, before a
   document-level listener ever sees the event**. Instrumenting the live page
   showed the order plainly: window capture fired, `/cart/add.js` went out, and
   only then did the document-capture listener run — cancelling an event that
   had already done its damage. So a listener cannot win this race by being
   careful; it can only win by being registered earlier than a component it does
   not control, which is not a property worth depending on.

   Replacing the node removes every listener attached to it, so the swapped-in
   button **cannot add to cart at all** — not because we cancel it in time, but
   because nothing is bound. That also makes the failure mode inherently safe:
   if the picker selector ever changes, the button does nothing and somebody
   reports a dead button, instead of silently reverting to the bypass.

   ⚠️ THIS CLOSES THE DOOR, NOT THE ROOM. It fixes the path a real customer
   takes. It is still front-end: anything that posts to /cart/add directly — a
   stale tab, a cached page, an old cart — can still make a line item with no
   time, and EAB has no checkout validation of its own (that was Appointo's
   feature). The actual guarantee is a cart-level guard that blocks checkout on
   a bookable line item with no time property. That is logged as the follow-up.
   ========================================================================= */

(() => {
  const PICKER = '.servicify-select-a-time-button';
  const STICKY = '.sticky-add-to-cart__button';
  const MARK = 'susannaBooking';

  const openPicker = (event) => {
    event.preventDefault();
    event.stopPropagation();
    document.querySelector(PICKER)?.click();
  };

  /* Horizon nests the words in `.add-to-cart-text__content`, beside an icon in
     a sibling `.svg-wrapper`; writing to that one element replaces the words
     and leaves the icon alone. The label is read from the picker rather than
     hardcoded, so it follows whatever language the app serves instead of
     drifting out of sync with the button beside it. */
  const relabel = (button) => {
    const label = document.querySelector(PICKER)?.textContent?.trim();
    const target = button.querySelector('.add-to-cart-text__content');
    if (label && target != null) target.textContent = label;
  };

  const swap = (button) => {
    if (button.dataset[MARK] === 'on') return;

    const replacement = button.cloneNode(true);
    replacement.removeAttribute('on:click'); // Horizon's declarative binding
    replacement.removeAttribute('ref'); // stops the component re-adopting it
    replacement.dataset[MARK] = 'on';

    relabel(replacement);
    replacement.addEventListener('click', openPicker);
    button.replaceWith(replacement);
  };

  const swapAll = () => {
    for (const button of document.querySelectorAll(STICKY)) swap(button);
  };

  /* The app boots asynchronously, so "is this product bookable?" cannot be
     answered on first paint, and Horizon can re-render the sticky bar later.
     Watch for both: arm once the picker exists, and keep watching so a
     re-rendered bar is swapped again rather than quietly restoring the bypass.
     On a package page the picker never appears, so the observer is dropped
     after a bounded wait instead of living for the whole session. */
  if (document.querySelector(PICKER) != null) {
    swapAll();
  }

  const observer = new MutationObserver(() => {
    if (document.querySelector(PICKER) != null) swapAll();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  setTimeout(() => {
    if (document.querySelector(PICKER) == null) observer.disconnect();
  }, 15000);

  /* The footer's sign-off CTA, on its own target page (added 2026-09-23).
     sections/alatunniste.liquid gives it the class `susanna-avaa-varaus` only
     when the button would otherwise link to the page it is on, so here it opens
     the picker instead — the same action as "Valitse aika". A plain <a> with no
     Horizon binding, so an ordinary delegated listener is enough; the capture
     race described above is specific to the sticky bar. With no picker on the
     page the click is left alone and the link does what its href says. */
  document.addEventListener('click', (event) => {
    const cta = event.target instanceof Element ? event.target.closest('.susanna-avaa-varaus') : null;
    if (cta == null || document.querySelector(PICKER) == null) return;
    openPicker(event);
  });
})();

/* ============================================================================
   Three strings in the booking panels that we cannot fix in the app
   ----------------------------------------------------------------------------
   Added 2026-09-23. Most of what the app prints is a display setting in its
   admin, but its Text Customization screen has NO FINNISH OPTION, and picking
   "Custom" prefills all 63 strings in English — saving it would replace every
   working Finnish string (docs/eab-known-issues.md §2). So a Finnish string
   cannot be edited one at a time there. These three are fixed here instead:

   1. "Confirming..." — the confirm button's busy label is HARDCODED English in
      the app bundle (`t ? "Confirming..." : <display setting>`), so a Finnish
      customer sees one English word at the exact moment they commit. The
      Finnish text is a theme setting (see CONFIRMING_FI below).
   2. "1 Osallistuja" on the confirmation card — the attendee count. Every
      session here is one-to-one, so the count says nothing; what a customer
      wants to see confirmed is WHICH session they booked. The line is replaced
      with the product's title, passed in from Liquid on this script's own tag
      (`data-tuote` in snippets/scripts.liquid).
   3. "(Europe/Helsinki)" in the "Valittu aika" summary — see dropTimezone.
      (The same label on the confirmation card has its own hook and is hidden
      in CSS instead.)

   HOW, and why it is safe against React. The text is changed by rewriting the
   existing TEXT NODES' values in place — never by replacing elements or
   setting `textContent` on a parent. React diffs against its own props, not
   against the DOM, so when it next updates that text (the button going back to
   "Vahvista varaus") it writes over our value as normal; nothing it holds a
   reference to has been removed. Each rewrite is guarded by a match on the
   ORIGINAL text, so the observer's own edits never re-trigger work.

   If the app ever translates "Confirming..." or renames the attendee label,
   the matches simply stop firing and the app's own text shows — no breakage.
   ========================================================================= */

(() => {
  const PICKER = '.servicify-select-a-time-button';
  const PORTAL = '.servicify-modal-portal';
  const CONFIRM_BUTTON = '[data-servicify="confirm-no-checkout-booking-button"]';
  const CONFIRMING = 'Confirming...';
  /* The Finnish replacement is OUR string, so it is a theme setting
     (`susanna_eab_vahvistetaan`), passed in on this script's own tag as
     `data-vahvistetaan` beside `data-tuote`. The literal is only the fallback
     for a tag or setting that is missing — it is today's text, so the page
     reads the same either way. Blank setting = fallback, never an empty
     button. */
  const CONFIRMING_FI = document.currentScript?.dataset.vahvistetaan?.trim() || 'Vahvistetaan varausta…';
  const ATTENDEE_LINE = /^\d+\s+Osallistuja$/;
  const PREVIEW_TIME = '[data-servicify="booking-preview-selected-time"]';
  const TIMEZONE_LABEL = /\s*\([A-Z][A-Za-z_]+\/[A-Za-z_/-]+\)/;
  const product = document.currentScript?.dataset.tuote?.trim() ?? '';

  /* Which coach the customer is booking (multi-coach build A2, decision M7,
     2026-10-05). `data-valmentajat` on this script's tag is a JSON map of
     variant id → coach name, built in Liquid from the variants'
     `custom.valmentaja` references (snippets/susanna-valmentaja-tiedot.liquid).
     With 2+ DISTINCT names the "Valittu aika" line below names the coach of
     the CURRENTLY selected variant instead of the product; with 0–1 names
     nothing here changes, so the single-coach site behaves exactly as before.
     The selected variant is re-resolved on every tidy pass — Horizon writes
     the id into the product form's hidden input[name=id] after a switch, and
     the URL's ?variant= is the fallback for a page without that form. A
     selected variant with no coach falls back to the product title. */
  const coachByVariant = (() => {
    try {
      const parsed = JSON.parse(document.currentScript?.dataset.valmentajat || '{}');
      return parsed != null && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  })();
  const manyCoaches = new Set(Object.values(coachByVariant).filter((name) => typeof name === 'string' && name !== '')).size >= 2;
  const selectedVariantId = () =>
    [...document.querySelectorAll('product-form-component input[name="id"]')]
      .find((input) => input.closest('product-card, quick-add-dialog, dialog') == null)
      ?.value?.trim() ||
    new URL(globalThis.location.href).searchParams.get('variant') ||
    '';
  const sessionName = () => {
    if (!manyCoaches) return product;
    const name = coachByVariant[selectedVariantId()];
    return typeof name === 'string' && name.trim() !== '' ? name.trim() : product;
  };

  const textNodes = (element) => {
    const nodes = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node != null; node = walker.nextNode()) nodes.push(node);
    return nodes;
  };

  const translateConfirming = (portal) => {
    for (const button of portal.querySelectorAll(CONFIRM_BUTTON)) {
      for (const node of textNodes(button)) {
        if (node.nodeValue.includes(CONFIRMING)) {
          node.nodeValue = node.nodeValue.replace(CONFIRMING, CONFIRMING_FI);
        }
      }
    }
  };

  /* The attendee line has no hook of its own. It is found as a LEAF element
     (no child elements) whose whole text is "<n> Osallistuja" — React splits it
     into several text nodes (`1`, ` `, `Osallistuja`), so the first takes the
     product title and the rest are emptied. */
  const nameTheSession = (portal) => {
    if (product === '') return;
    for (const element of portal.querySelectorAll('div, p, span')) {
      if (element.childElementCount > 0) continue;
      if (!ATTENDEE_LINE.test(element.textContent.trim())) continue;
      const [first, ...rest] = textNodes(element);
      if (first == null) continue;
      first.nodeValue = product;
      for (const node of rest) node.nodeValue = '';
    }
  };

  /* "Valittu aika" summary above the details form: "24. syyskuuta 2026 klo
     14.30 - 15.00 (Europe/Helsinki) – Starttikartoitus". The timezone is noise
     on a Finnish-only store. It is one text run built from the app's
     CAL_CONFIRMATION_SUBTITLE string, so CSS cannot reach it, and the string
     cannot be edited in the app either (see the header). Matched as an IANA
     zone name in brackets, so it works for whatever zone the event is set to. */
  /* The same line ENDS with the app's staff-member name ("– {{{employee}}}").
     That was "Starttikartoitus" on every event until 2026-09-23 — the one
     availability record had been named after the first product — so a
     60-minute session was summarised as "– Starttikartoitus". The record is
     now "Susanna", but what a reader checking their choice needs here is WHAT
     they are booking, and the confirmation card already names the product
     (below), so the line does too. The name after the last dash is replaced
     with the product title; the guard on `endsWith` keeps the observer from
     rewriting its own output. EN DASH ONLY: the Finnish string separates the
     name with "–", while the time range uses a hyphen ("13.00 - 14.00"), so a
     pattern that also took "-" would eat the end time if the name were ever
     missing. */
  const STAFF_SUFFIX = /\s–\s[^–]+$/;

  /* `label` is the product title, or on a product with 2+ coaches the selected
     coach's name (see sessionName above). The guard is against the CURRENT
     label — the only thing this pass would write — so the observer never
     rewrites its own output, while a line still ending in the app's staff
     name, the product title or a previously selected coach is brought up to
     date. */
  const dropTimezone = (portal) => {
    const label = sessionName();
    for (const box of portal.querySelectorAll(PREVIEW_TIME)) {
      for (const node of textNodes(box)) {
        if (TIMEZONE_LABEL.test(node.nodeValue)) {
          node.nodeValue = node.nodeValue.replace(TIMEZONE_LABEL, '');
        }
        if (label !== '' && !node.nodeValue.endsWith(label) && STAFF_SUFFIX.test(node.nodeValue)) {
          node.nodeValue = node.nodeValue.replace(STAFF_SUFFIX, ` – ${label}`);
        }
      }
    }
  };

  const tidy = () => {
    for (const portal of document.querySelectorAll(PORTAL)) {
      translateConfirming(portal);
      nameTheSession(portal);
      dropTimezone(portal);
    }
  };

  const observer = new MutationObserver(tidy);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  /* Same bounded wait as above: a page that never grows a picker has no
     booking panels to tidy, so the observer does not outlive it. */
  setTimeout(() => {
    if (document.querySelector(PICKER) == null) observer.disconnect();
  }, 15000);
})();
